class ProductViewer360 extends HTMLElement {
  
  static get observedAttributes() {
    return ['data-active'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch(name) {
      case 'data-active':
        this.handleActiveChange(newValue);
        break;
    }
  }
  
  handleActiveChange (newValue) {
    if (newValue === 'true') {
      this.resetToFirstImage();
      this.run();
    }
  }
  
  /**
   * 重置 360 视图到第一张图片
   * 当用户切换颜色或 edition 时调用
   */
  resetToFirstImage() {
    this.state.currentImageIndex = 0;
    // 如果图片已经加载完成，立即显示第一张
    if (this.options.hasLoadedImages && this.images && this.images.length > 0) {
      this.showImage(0);
    }
    // 如果正在自动播放，停止播放
    if (this.state.isPlaying) {
      this.pause();
    }
  }
  
  constructor() {
    super();
    
    this.options = {
      autoplay: this.dataset.autoplay === 'true',
      speed: parseInt(this.dataset.speed) || 100,
      sensitivity: parseInt(this.dataset.sensitivity) || 10,
      reverse: this.dataset.reverse === 'true',
      hoverReenableDelay: 1500,
      replaceElementSelector: this.dataset.replaceElementSelector || null,
      clickToHover: this.dataset.clickToHover === 'true',
      active: this.dataset.active === 'true',
      hasLoadedImages: false
    };

    this.state = {
      currentImageIndex: 0,
      isPlaying: false,
      isDragging: false,
      isHoverDisabled: false,
      lastPosition: null,
      accumulatedDelta: 0,
      initialClick: false,
      isTouching: false,
      hasUserInteracted: false, // Track if user has interacted on mobile
      isHorizontalSwipe: null // null = not determined, true = horizontal, false = vertical
    };
    
    // 方向锁定阈值（像素）
    this.directionLockThreshold = 10;

    this.timeouts = {
      cursor: null,
      hoverRenable: null,
      playInterval: null
    };
    
    Object.defineProperty(this.options, 'active', {
      get: () => this.state.currentEdition,
      set: (value) => { this.state.currentEdition = value; }
    });
    
    
    if (this.options.active) {
      this.run();
    }
  }
  
  connectedCallback() {
  }
  
  run () {
    document.readyState === "loading"
    ? document.addEventListener("DOMContentLoaded", () => this.setup())
    : this.setup();
  }

  async setup() {
    this.replaceElementIfExist();
    this.imageElement = this.querySelector('.image-viewer-360__image');
    this.loadingElement = this.querySelector('.image-viewer-360__loading');
    this.progressFill = this.querySelector('.image-viewer-360__progress-fill');
    this.cursor = this.querySelector('.image-viewer-360__cursor');
    this.touchStartY = 0;

    // 等待图片加载完成后再启用 360 功能
    await this.preloadImages();
    if (!this.options.hasLoadedImages) return;
    console.log('images loaded');
    
    this.addEventListeners();
    this.updateCursorState('grab');
    if (this.options.autoplay) {
      this.play();
    }
  }

  replaceElementIfExist() {
    const elementToReplace = document.querySelector(this.options.replaceElementSelector);
    if (elementToReplace) {
      elementToReplace.parentNode.replaceChild(this, elementToReplace);
      console.log(`360 viewer has replaced element with selector "${this.options.replaceElementSelector}".`);
    }
  }
  
  async preloadImages() {
    if (this.options.hasLoadedImages) return;
    
    const imageUrls = this.getImageUrls();
    
    // 初始化加载状态追踪变量
    this.loadedImages = 0;
    this.totalImages = imageUrls.length;
    this.successfullyLoadedImages = 0;
    this.failedImages = 0;
    this.images = new Array(imageUrls.length);
    
    if (imageUrls.length === 0) {
      console.warn('No image URLs provided for the image viewer 360');
      this.hideLoading();
      return;
    }
    
    const CONCURRENT_LIMIT = 6; // 控制并发数
    
    // 使用并发控制函数
    await this.loadImagesWithConcurrency(imageUrls, CONCURRENT_LIMIT);
    
    // 检查图片是否100%加载完成
    if (!this.isImagesFullyLoaded()) {
      console.warn('360 Viewer: Images did not fully load');
      return;
    }
    
    // 检查是否可以启用360功能
    if (!this.canEnable360()) {
      console.error('360 Viewer: Too many images failed to load, 360 feature disabled');
      return;
    }
    
    // 输出加载状态
    const status = this.getLoadingStatus();
    console.log(`360 Viewer: Images loaded - ${status.successful}/${status.total} successful (${status.progress}%)`);
    
    this.hideLoading();
    this.showImage(0);
    this.options.hasLoadedImages = true;
  }
  
  async loadImagesWithConcurrency(urls, limit) {
    const results = [];
    const executing = [];
    
    for (const [index, url] of urls.entries()) {
      const promise = this.loadSingleImage(url, index).then(img => {
        // 从执行队列中移除
        executing.splice(executing.indexOf(promise), 1);
        return img;
      });
      
      results.push(promise);
      executing.push(promise);
      
      // 当并发数达到限制时，等待一个完成
      if (executing.length >= limit) {
        await Promise.race(executing);
      }
    }
    
    return Promise.all(results);
  }
  
  loadSingleImage(url, index) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.loadedImages++;
        this.successfullyLoadedImages++;
        this.updateLoadingProgress();
        this.images[index] = img;
        resolve(img);
      };
      img.onerror = (error) => {
        console.error(`Failed to load image: ${url}`, error);
        this.loadedImages++;
        this.failedImages++;
        this.updateLoadingProgress();
        reject(error);
      };
      img.src = url;
    });
  }
  
  /**
   * 判断图片是否100%完成加载
   * @returns {boolean} 所有图片是否已加载完成
   */
  isImagesFullyLoaded() {
    return this.loadedImages === this.totalImages && this.totalImages > 0;
  }
  
  /**
   * 判断图片是否成功加载（排除失败的图片）
   * @returns {boolean} 所有图片是否成功加载（无失败）
   */
  isImagesSuccessfullyLoaded() {
    return this.isImagesFullyLoaded() && this.failedImages === 0;
  }
  
  /**
   * 获取当前加载进度百分比
   * @returns {number} 0-100 的进度值
   */
  getLoadingProgress() {
    if (this.totalImages === 0) return 0;
    return Math.round((this.loadedImages / this.totalImages) * 100);
  }
  
  /**
   * 获取加载状态详情
   * @returns {Object} 包含 total, loaded, successful, failed, progress, isComplete 的状态对象
   */
  getLoadingStatus() {
    return {
      total: this.totalImages || 0,
      loaded: this.loadedImages || 0,
      successful: this.successfullyLoadedImages || 0,
      failed: this.failedImages || 0,
      progress: this.getLoadingProgress(),
      isComplete: this.isImagesFullyLoaded(),
      isSuccessful: this.isImagesSuccessfullyLoaded()
    };
  }
  
  /**
   * 检查是否可以启用360功能
   * 只有当图片100%加载完成且至少有一定比例成功时才返回true
   * @param {number} minSuccessRate - 最小成功率（0-1），默认1（100%）
   * @returns {boolean} 是否可以启用360功能
   */
  canEnable360(minSuccessRate = 1) {
    if (!this.isImagesFullyLoaded()) return false;
    const successRate = this.successfullyLoadedImages / this.totalImages;
    return successRate >= minSuccessRate;
  }

  isMobile() {
    return window.innerWidth <= 900;
  }

  getImageUrls() {
    const isMobile = this.isMobile();
    const mobileUrls = this.dataset.imageUrlsMobile ? this.dataset.imageUrlsMobile.split(',') : [];
    if (isMobile && mobileUrls.length === 0) {
      console.warn('No mobile image URLs provided for the image viewer 360');
    }
    const desktopUrls = this.dataset.imageUrls ? this.dataset.imageUrls.split(',') : [];
    if (desktopUrls.length === 0) {
      console.warn('No desktop image URLs provided for the image viewer 360');
    }
    return isMobile && mobileUrls.length > 0 ? mobileUrls : desktopUrls;
  }

  updateLoadingProgress() {
    const progress = (this.loadedImages / this.images.length) * 100;
    this.progressFill.style.width = `${progress}%`;
  }

  hideLoading() {
    this.loadingElement.style.display = 'none';
  }

  showImage(index) {
    if (index >= 0 && index < this.images.length) {
      this.state.currentImageIndex = index;
      this.imageElement.src = this.images[index].src;
    }
  }

  addEventListeners() {
    this.addEventListener('mouseenter', () => {
      // 桌面端鼠标进入时显示 grab cursor（移动端不处理）
      if (this.options.hasLoadedImages && !this.isMobile()) {
        this.updateCursorState('grab');
      }
    });
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.addEventListener('mousemove', this.onMouseMove.bind(this));
    this.addEventListener('mousedown', this.onDragStart.bind(this));
    this.addEventListener('mouseup', this.onDragEnd.bind(this));

    if ('ontouchstart' in window) {
      this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
      this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false }); // passive: false 允许调用 preventDefault()
      this.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    }
  }

  onMouseLeave() {
    this.updateCursorState('hide');
    this.onDragEnd(true); // skipCursorUpdate = true, 保持 cursor 隐藏状态
    if (this.options.autoplay) {
      this.play();
    }
  }

  updateCursorState(state) {
    const cursorStates = {
      hide: { text: '', cursor: 'pointer', opacity: 0 },
      grab: { text: 'Grab', cursor: 'grab', opacity: 0.8 },
      grabbing: { text: 'Grab', cursor: 'grabbing', opacity: 0.8 },
    };

    const { text, cursor, opacity } = cursorStates[state];
    this.cursor.textContent = text;
    this.style.cursor = cursor;
    this.cursor.style.opacity = opacity;
    this.cursor.classList.toggle('image-viewer-360__cursor--active', state === 'grabbing');
    this.cursor.style.display = state === 'hide' ? 'none' : 'flex';
  }

  hasTouch() {
    return 'ontouchstart' in document.documentElement
          || navigator.maxTouchPoints > 0
          || navigator.msMaxTouchPoints > 0;
  }

  moveCursor(e) {
    // Get the mouse's current position and update cursor directly
    const targetX = this.getEventPositionX(e);
    const targetY = this.getEventPositionY(e);

    // Update the cursor position immediately (no delay)
    this.cursor.style.left = `${targetX}px`;
    this.cursor.style.top = `${targetY}px`;
  }

  onTouchStart = (e) => {
    // 移动端触摸开始时隐藏 grab 元素，并标记用户已交互
    this.updateCursorState('hide');
    this.state.hasUserInteracted = true;
    this.state.initialClick = true;
    this.state.isTouching = true;
    
    // 记录触摸起始位置，用于方向判断
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.state.lastPosition = this.touchStartX;
    this.state.accumulatedDelta = 0;
    
    // 重置方向锁定状态
    this.state.isHorizontalSwipe = null;
  };

  onTouchMove = (e) => {
    // 移动端触摸移动时保持 grab 元素隐藏，不移动 cursor
    this.updateCursorState('hide');
    this.state.initialClick = true;
    
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = Math.abs(currentX - this.touchStartX);
    const diffY = Math.abs(currentY - this.touchStartY);
    
    // 如果还没确定滑动方向，且移动距离超过阈值，则确定方向
    if (this.state.isHorizontalSwipe === null && 
        (diffX > this.directionLockThreshold || diffY > this.directionLockThreshold)) {
      this.state.isHorizontalSwipe = diffX > diffY;
    }
    
    // 如果是水平滑动，阻止页面垂直滚动并处理图片旋转
    if (this.state.isHorizontalSwipe === true) {
      if (e.cancelable) {
        e.preventDefault();
      }
      this.handleInteraction(currentX);
      this.state.lastPosition = currentX;
    }
    // 如果是垂直滑动，允许页面正常滚动，不处理图片旋转
  };
  
  onTouchEnd = () => {
    // 移动端放开手指后不再显示 grab cursor（用户已交互过）
    this.state.isTouching = false;
    this.state.isDragging = false;
    this.state.isHorizontalSwipe = null; // 重置方向锁定
    // 用户已交互过，保持 cursor 隐藏状态
    this.updateCursorState('hide');
    this.timeouts.hoverRenable = setTimeout(() => {
      this.state.isHoverDisabled = false;
    }, this.options.hoverReenableDelay);
  };

  onMouseMove(e) {
    this.moveCursor(e);
    const currentPositionX = this.getEventPositionX(e);

    if ((this.state.isDragging && e.type === 'mousemove') || (e.type === 'touchmove')) {
      this.handleInteraction(currentPositionX);
      this.updateCursorState(this.state.isDragging ? 'grabbing' : 'grab');
    }

    if (!this.state.isDragging) {
      this.pause();
    }
    this.state.lastPosition = currentPositionX;
  }

  handleInteraction(currentPosition) {
    if (this.options.clickToHover && !this.state.initialClick) return false;
    if (this.state.lastPosition !== null) {
      const delta = currentPosition - this.state.lastPosition;
      this.state.accumulatedDelta += delta;

      if (Math.abs(this.state.accumulatedDelta) > this.options.sensitivity) {
        this.rotateImage(this.state.accumulatedDelta > 0 ? -1 : 1);
        this.state.accumulatedDelta = 0;
      }
    }
  }

  onDragStart(e) {
    e.preventDefault();
    this.state.initialClick = true;
    this.state.isDragging = true;
    this.state.isHoverDisabled = true;
    clearTimeout(this.timeouts.hoverRenable);
    this.state.lastPosition = this.getEventPositionX(e);
    this.state.accumulatedDelta = 0;
    this.updateCursorState('grabbing');
    this.pause();
  }

  onDragEnd(skipCursorUpdate = false) {
    this.state.isDragging = false;
    // 检查参数是否为布尔值 true，避免 event 对象被误判
    const shouldSkipCursorUpdate = skipCursorUpdate === true;
    if (!shouldSkipCursorUpdate) {
      this.updateCursorState('grab');
    }
    this.timeouts.hoverRenable = setTimeout(() => {
      this.state.isHoverDisabled = false;
    }, this.options.hoverReenableDelay);
  }

  getEventPositionX(e) {
    const rect = this.getBoundingClientRect();
    if (e.touches !== undefined) {
      return e.touches[0].clientX - rect.left;
    }
    return e.clientX - rect.left;
  }

  getEventPositionY(e) {
    const rect = this.getBoundingClientRect();
    if (e.touches !== undefined) {
      return e.touches[0].clientY - rect.top;
    }
    return e.clientY - rect.top;
  }

  rotateImage(direction) {
    const newIndex = (this.state.currentImageIndex + direction + this.images.length) % this.images.length;
    this.showImage(newIndex);
  }

  play() {
    if (!this.state.isPlaying) {
      this.timeouts.playInterval = setInterval(() => this.rotateImage(this.options.reverse ? -1 : 1), this.options.speed);
      this.state.isPlaying = true;
    }
  }

  pause() {
    clearInterval(this.timeouts.playInterval);
    this.state.isPlaying = false;
  }
}

customElements.define('image-viewer-360', ProductViewer360);
